import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { trackUserLogin, trackUserRegistration } from '../utils/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Token management utilities
const tokenManager = {
  setTokens: (tokens) => {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    
    // Calculate expiry based on the actual token or default to 15 minutes
    const expiryTime = tokens.expiresIn ? 
      Date.now() + (parseInt(tokens.expiresIn.replace('m', '')) * 60 * 1000) :
      Date.now() + (15 * 60 * 1000); // Default 15 minutes
    
    localStorage.setItem('tokenExpiry', expiryTime);
  },

  getAccessToken: () => {
    const token = localStorage.getItem('accessToken');
    return token;
  },
  
  getRefreshToken: () => {
    const token = localStorage.getItem('refreshToken');
    return token;
  },
  
  clearTokens: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');
  },

  isTokenExpired: () => {
    const expiry = localStorage.getItem('tokenExpiry');
    const isExpired = expiry ? Date.now() > parseInt(expiry) : true;
    return isExpired;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on app start
  useEffect(() => {
    checkAuth();
  }, []);

  // Set up axios interceptor for automatic token attachment
  useEffect(() => {
    
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        const token = tokenManager.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = tokenManager.getRefreshToken();
            if (refreshToken) {
              const response = await api.post('/auth/refresh-token', {
                refreshToken: refreshToken
              });

              const { tokens } = response.data;
              tokenManager.setTokens(tokens);

              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
              return api(originalRequest);
            }
          } catch (refreshError) {
            tokenManager.clearTokens();
            setUser(null);
            
            // Only redirect if not already on login/signup page
            const currentPath = window.location.pathname;
            const publicPaths = ['/', '/login', '/signup'];
            if (!publicPaths.includes(currentPath)) {
              window.location.href = '/login';
            }
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const token = tokenManager.getAccessToken();
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Check if token is expired and try to refresh if needed
      if (tokenManager.isTokenExpired()) {
        const refreshToken = tokenManager.getRefreshToken();
        
        if (refreshToken) {
          try {
            const response = await api.post('/auth/refresh-token', {
              refreshToken: refreshToken
            });

            const { tokens } = response.data;
            tokenManager.setTokens(tokens);
          } catch (refreshError) {
            tokenManager.clearTokens();
            setUser(null);
            setLoading(false);
            return;
          }
        } else {
          tokenManager.clearTokens();
          setUser(null);
          setLoading(false);
          return;
        }
      }

      const response = await api.get('/auth/me');
      setUser(response.data.user);
    } catch (error) {
      
      // If it's a 401 error, try to refresh token one more time
      if (error.response?.status === 401) {
        const refreshToken = tokenManager.getRefreshToken();
        
        if (refreshToken) {
          try {
            const refreshResponse = await api.post('/auth/refresh-token', {
              refreshToken: refreshToken
            });

            const { tokens } = refreshResponse.data;
            tokenManager.setTokens(tokens);
            
            // Retry the auth check with new token
            const retryResponse = await api.get('/auth/me');
            setUser(retryResponse.data.user);
            setLoading(false);
            return;
          } catch (fallbackError) {
          }
        }
      }
      
      tokenManager.clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      // Store JWT tokens
      if (response.data.tokens) {
        tokenManager.setTokens(response.data.tokens);
      }
      
      setUser(response.data.user);
      
      // Track successful login
      trackUserLogin('email');
      
      return { success: true, user: response.data.user };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      // Clear tokens and user state
      tokenManager.clearTokens();
      setUser(null);
      return { success: true };
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
    // Expose token manager for advanced use cases
    tokenManager
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};